/**
 * A maintenance plan's interval and next due date — build 6B.
 *
 * A new interval counts from the last time the work was done; a due date moved
 * by hand stays moved (the platform's maintenance job no longer puts it back).
 * Who changed it and when is in the asset's history.
 */
import { useState } from "react";
import { useAmEditPlan } from "../../api/queries";
import { Button, Field, Input } from "../../design/components";
import type { AmPlan } from "../../domain/assetManagement";
import { planPatchRefusal, refusalText } from "../../domain/entryForms";
import { t } from "../../i18n";
import s from "./EntryForms.module.css";

export function PlanDatesForm({ plan, onDone }: { plan: AmPlan; onDone: (msg: string | null) => void }) {
  const edit = useAmEditPlan();
  const calendar = plan.trigger === "calendar";
  const [every, setEvery] = useState(plan.intervalDays != null ? String(plan.intervalDays) : "");
  const [due, setDue] = useState(plan.nextDueAt ? plan.nextDueAt.slice(0, 10) : "");
  const [why, setWhy] = useState<string | null>(null);

  const save = async () => {
    const patch: { intervalDays?: number; nextDueOn?: string | null } = {};
    if (calendar && every.trim() !== String(plan.intervalDays ?? ""))
      patch.intervalDays = every.trim() === "" ? Number.NaN : Number(every);
    if (due !== (plan.nextDueAt ? plan.nextDueAt.slice(0, 10) : "")) patch.nextDueOn = due || null;
    if (!Object.keys(patch).length) return onDone(null);
    const refused = planPatchRefusal(plan.trigger, patch);
    if (refused) return setWhy(refused);
    setWhy(null);
    try {
      await edit.mutateAsync({ id: plan.id, patch });
      onDone(t("{plan} on {tag}: saved.", { plan: plan.title, tag: plan.tag ?? "" }));
    } catch (e) {
      setWhy(refusalText(e));
    }
  };

  return (
    <div className={s.form} data-testid="plan-dates-form">
      {calendar ? (
        <Field label="Every (days)">
          <Input type="number" min={1} max={3650} step={1} value={every} onChange={(e) => setEvery(e.target.value)} />
        </Field>
      ) : (
        <p className={s.muted}>{t("This plan runs on usage, so it has no interval in days.")}</p>
      )}
      <Field label="Next due">
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      </Field>
      {calendar && plan.lastDoneAt && (
        <p className={`${s.muted} ${s.wide}`}>{t("A new interval counts from the last time it was done.")}</p>
      )}
      {why && (
        <p className={`${s.err} ${s.wide}`} role="alert">
          {why}
        </p>
      )}
      <div className={`${s.actions} ${s.wide}`}>
        <Button variant="primary" onClick={() => void save()} disabled={edit.isPending}>
          {edit.isPending ? t("Saving…") : t("Save plan")}
        </Button>
        <Button variant="ghost" onClick={() => onDone(null)}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
